/**
 * Help Center Tour - Guide users through help resources and support
 * @author Ellis Reed
 */

"use client";

import React, { useState, useEffect, useCallback } from "react";
import Joyride, { CallBackProps, STATUS, Step, ACTIONS, EVENTS } from "react-joyride";

const STORAGE_KEYS = { completed: "usland-help-center-tour-completed", skipped: "usland-help-center-tour-skipped" };

interface HelpCenterTourProps {
  onComplete?: () => void;
  shouldRun?: boolean;
}

export function HelpCenterTour({ onComplete, shouldRun = false }: HelpCenterTourProps) {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (shouldRun) {
      const timer = setTimeout(() => setRun(true), 500);
      return () => clearTimeout(timer);
    }
  }, [shouldRun]);

  const steps: Step[] = [
    {
      target: "body",
      content: (
        <div className="space-y-3">
          <h2 className="text-2xl font-bold text-gray-900">Help Center</h2>
          <p className="text-gray-700">Get support and find answers to your questions.</p>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mt-2">
            <p className="text-xs text-blue-900"><strong>Multiple ways to get help:</strong> Articles, AI chat, and direct support.</p>
          </div>
        </div>
      ),
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="help-categories"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Help Categories</h3>
          <p className="text-gray-700">Browse organized help articles by topic.</p>
          <ul className="text-sm text-gray-600 list-disc list-inside">
            <li>Getting Started guides</li>
            <li>Listing Management</li>
            <li>Account & Billing</li>
            <li>Technical Support</li>
          </ul>
        </div>
      ),
      placement: "bottom",
      spotlightPadding: 10,
    },
    {
      target: '[data-tour="search-articles"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Search Articles</h3>
          <p className="text-gray-700">Find specific answers quickly with full-text search.</p>
          <div className="bg-purple-50 border border-purple-200 rounded-md p-2 mt-2">
            <p className="text-xs text-purple-900"><strong>Pro tip:</strong> Press Cmd+K (or Ctrl+K) from anywhere to search.</p>
          </div>
        </div>
      ),
      placement: "bottom",
      spotlightPadding: 8,
    },
    {
      target: '[data-tour="ai-chat-button"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">AI Chat Assistant</h3>
          <p className="text-gray-700">Get instant answers from our AI-powered help assistant.</p>
          <ul className="text-sm text-gray-600 list-disc list-inside">
            <li>24/7 availability</li>
            <li>Natural language questions</li>
            <li>Contextual answers</li>
            <li>Links to detailed articles</li>
          </ul>
          <div className="bg-green-50 border border-green-200 rounded-md p-2 mt-2">
            <p className="text-xs text-green-900"><strong>Smart:</strong> AI learns from help articles for accurate answers.</p>
          </div>
        </div>
      ),
      placement: "bottom",
      spotlightPadding: 10,
    },
    {
      target: '[data-tour="contact-support"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Contact Support</h3>
          <p className="text-gray-700">Can't find what you need? Reach our support team directly.</p>
        </div>
      ),
      placement: "top",
      spotlightPadding: 8,
    },
    {
      target: "body",
      content: (
        <div className="space-y-3">
          <h2 className="text-2xl font-bold text-gray-900">Help at Your Fingertips!</h2>
          <p className="text-gray-700">Use these resources whenever you need assistance.</p>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-2">
            <p className="text-sm font-medium text-blue-900">Remember:</p>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>* Press Cmd+K (Ctrl+K) to search from anywhere</li>
              <li>* Try AI chat for quick answers</li>
              <li>* Browse articles for detailed guides</li>
              <li>* Contact support for personalized help</li>
            </ul>
          </div>
        </div>
      ),
      placement: "center",
    },
  ];

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setRun(false);
      if (status === STATUS.FINISHED) localStorage.setItem(STORAGE_KEYS.completed, "true");
      else localStorage.setItem(STORAGE_KEYS.skipped, "true");
      onComplete?.();
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }
  }, [onComplete]);

  const restartTour = useCallback(() => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(STORAGE_KEYS.completed);
      localStorage.removeItem(STORAGE_KEYS.skipped);
    }
    setStepIndex(0);
    setRun(true);
  }, []);

  useEffect(() => {
    window.addEventListener("restart-help-center-tour", restartTour);
    return () => window.removeEventListener("restart-help-center-tour", restartTour);
  }, [restartTour]);

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      styles={{
        options: { primaryColor: "#0066cc", textColor: "#1f2937", backgroundColor: "#ffffff", zIndex: 10000 },
        tooltip: { borderRadius: 8, padding: 20 },
        buttonNext: { backgroundColor: "#0066cc", fontSize: 14, fontWeight: 600, padding: "10px 20px" },
      }}
      locale={{ back: "Back", close: "Close", last: "Finish", next: "Next", skip: "Skip tour" }}
      disableScrolling
      disableOverlayClose
      spotlightClicks
    />
  );
}

export function restartHelpCenterTour() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("restart-help-center-tour"));
}

export function isHelpCenterTourCompleted(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEYS.completed) === "true" || localStorage.getItem(STORAGE_KEYS.skipped) === "true";
}
